import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

/**
 * Structured-credit tranche slide-over drawer for the CLO Positions tab.
 *
 * Opens on a position row click and fetches
 * GET /api/credit/clo/positions/{positionId}/tranche-detail — deal/tranche
 * reference (attachment/detachment, factor, credit enhancement, ratings,
 * spread), the OC/IC coverage ladder, and an honest "Cashflows" co-build tab.
 *
 * All reference fields are read-only and sourced from fact_position_clo_attrs
 * + clo_tranche + clo_deal — no parallel store. See CLOManagementService
 * #getPositionTrancheDetail.
 */

export interface CloDrawerPosition {
  positionId: number;
  securityId: number;
  cusip: string;
  securityName: string;
  portfolioName: string;
  quantity: number;
  costBasis: number;
  marketValue: number;
  unrealizedPnl: number;
}

interface CoverageTest {
  trancheName: string;
  trancheType: string;
  ocActual: number; ocTrigger: number; ocCushion: number; ocStatus: string;
  icActual: number; icTrigger: number; icCushion: number; icStatus: string;
}

interface TrancheDetail {
  positionId: number;
  trancheId?: number;
  dealId?: number;
  dealName?: string;
  managerName?: string;
  trancheName?: string;
  trancheType?: string;
  paymentPriority?: number;
  attachmentPoint?: number;
  detachmentPoint?: number;
  creditEnhancement?: number;
  trancheThickness?: number;
  originalBalance?: number;
  currentBalance?: number;
  factor?: number;
  spreadBps?: number;
  couponType?: string;
  floorRate?: number;
  moodysRating?: string;
  spRating?: string;
  fitchRating?: string;
  wal?: number;
  paymentFrequency?: string;
  nextPaymentDate?: string;
  cusipSource?: string;
  ocActual?: number; ocTrigger?: number; ocCushion?: number; ocStatus?: string;
  icActual?: number; icTrigger?: number; icCushion?: number; icStatus?: string;
  dealCoverageTests?: CoverageTest[];
}

type Tab = 'overview' | 'surveillance' | 'cashflows';

const Overlay = styled(motion.div)`
  position: fixed; inset: 0; z-index: 1200;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(2px);
`;

const Panel = styled(motion.div)`
  position: fixed; top: 0; right: 0; bottom: 0; z-index: 1201;
  width: 480px; max-width: 92vw;
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 100%);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: -16px 0 48px rgba(0, 0, 0, 0.45);
  display: flex; flex-direction: column;
  color: #e2e8f0;
`;

const PanelHeader = styled.div`
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Eyebrow = styled.div`
  font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.08em;
  color: #8b5cf6; font-weight: 700; margin-bottom: 0.35rem;
`;

const DealTitle = styled.div` font-size: 1.05rem; font-weight: 700; color: #f1f5f9; `;
const SubTitle = styled.div` font-size: 0.8rem; color: #94a3b8; margin-top: 0.2rem; `;

const CloseBtn = styled.button`
  position: absolute; top: 1.1rem; right: 1.1rem;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
  color: #cbd5e1; width: 30px; height: 30px; border-radius: 8px; cursor: pointer;
  font-size: 1rem; line-height: 1;
  &:hover { background: rgba(255,255,255,0.12); }
`;

const Tabs = styled.div`
  display: flex; gap: 4px; padding: 0.75rem 1.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const TabBtn = styled.button<{ $active: boolean }>`
  padding: 0.5rem 0.85rem; font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.04em;
  border: 0; background: transparent; cursor: pointer;
  color: ${p => (p.$active ? '#c4b5fd' : '#64748b')};
  border-bottom: 2px solid ${p => (p.$active ? '#8b5cf6' : 'transparent')};
  &:hover { color: #e2e8f0; }
`;

const Body = styled.div` padding: 1.25rem 1.5rem; overflow-y: auto; flex: 1; `;

const Grid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem 1.25rem;
  margin-bottom: 1.25rem;
`;
const Field = styled.div``;
const FLabel = styled.div`
  font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.05em;
  color: #94a3b8; margin-bottom: 0.2rem;
`;
const FValue = styled.div<{ $mono?: boolean }>`
  font-size: 0.9rem; font-weight: 600; color: #e2e8f0;
  font-variant-numeric: tabular-nums;
  ${p => p.$mono && 'font-family: monospace;'}
`;

const SectionTitle = styled.div`
  font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em;
  color: #cbd5e1; font-weight: 700; margin: 0.5rem 0 0.75rem;
`;

/* Capital-structure attach/detach bar */
const StackBar = styled.div`
  position: relative; height: 26px; border-radius: 6px; overflow: hidden;
  background: rgba(148, 163, 184, 0.15); margin: 0.4rem 0 0.3rem;
  border: 1px solid rgba(255,255,255,0.08);
`;
const StackFill = styled.div<{ $left: number; $width: number }>`
  position: absolute; top: 0; bottom: 0;
  left: ${p => p.$left}%; width: ${p => p.$width}%;
  background: linear-gradient(135deg, rgba(139,92,246,0.85), rgba(139,92,246,0.55));
`;
const StackCaption = styled.div` font-size: 0.68rem; color: #94a3b8; `;

const TestRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.55rem 0.7rem; border-radius: 8px; margin-bottom: 0.45rem;
  background: rgba(15, 23, 42, 0.5); border: 1px solid rgba(255,255,255,0.06);
`;
const TestName = styled.div` font-size: 0.8rem; color: #e2e8f0; font-weight: 600; `;
const TestVals = styled.div<{ $pass: boolean }>`
  font-size: 0.75rem; font-variant-numeric: tabular-nums;
  color: ${p => (p.$pass ? '#34d399' : '#f87171')}; font-weight: 600;
`;

const Empty = styled.div`
  text-align: center; color: #64748b; font-size: 0.85rem; padding: 1.5rem 0.5rem;
`;
const CoBuild = styled.div`
  border: 1px dashed rgba(139, 92, 246, 0.4); border-radius: 10px;
  padding: 1.25rem; background: rgba(139, 92, 246, 0.06);
  color: #cbd5e1; font-size: 0.83rem; line-height: 1.5;
`;
const CoBuildTag = styled.div`
  display: inline-block; font-size: 0.62rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em; color: #c4b5fd;
  background: rgba(139, 92, 246, 0.18); border: 1px solid rgba(139,92,246,0.35);
  padding: 0.2rem 0.5rem; border-radius: 999px; margin-bottom: 0.6rem;
`;

// attachment/detachment may be stored as a fraction (0.12) or a percent (12.0).
// Heuristic: values at/below 1.5 are treated as fractions. CLO attachment
// points are never ~1% so this is safe across the realistic range.
const pct = (v?: number | null): string => {
  if (v == null) return '—';
  const p = Math.abs(v) <= 1.5 ? v * 100 : v;
  return `${p.toFixed(1)}%`;
};
const pctNum = (v?: number | null): number => {
  if (v == null) return 0;
  return Math.abs(v) <= 1.5 ? v * 100 : v;
};
const bps = (v?: number | null): string => (v == null ? '—' : `+${Number(v).toFixed(0)} bps`);
const num = (v?: number | null, d = 4): string => (v == null ? '—' : Number(v).toFixed(d));
const money = (v?: number | null): string =>
  v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const ratings = (d: TrancheDetail): string =>
  [d.moodysRating, d.spRating, d.fitchRating].filter(Boolean).join(' / ') || 'NR';

const CloTrancheDrawer: React.FC<{ position: CloDrawerPosition | null; onClose: () => void }> = ({ position, onClose }) => {
  const [tab, setTab] = useState<Tab>('overview');
  const [detail, setDetail] = useState<TrancheDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    if (!position) return;
    setTab('overview');
    setDetail(null);
    setNoData(false);
    setLoading(true);
    axios
      .get(`/api/credit/clo/positions/${position.positionId}/tranche-detail`)
      .then(res => {
        if (res.status === 204 || !res.data) { setNoData(true); return; }
        setDetail(res.data);
      })
      .catch(() => setNoData(true))
      .finally(() => setLoading(false));
  }, [position]);

  // Esc to close
  useEffect(() => {
    if (!position) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [position, onClose]);

  const ladder = detail?.dealCoverageTests || [];
  const isEquity = (detail?.trancheType || '').toUpperCase() === 'EQUITY'
    || (detail?.trancheName || '').toUpperCase().includes('SUB')
    || detail?.ocActual == null;

  return (
    <AnimatePresence>
      {position && (
        <>
          <Overlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <Panel
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
          >
            <PanelHeader>
              <CloseBtn onClick={onClose}>×</CloseBtn>
              <Eyebrow>Tranche Detail</Eyebrow>
              <DealTitle>
                {detail?.dealName || position.securityName}
                {detail?.trancheName ? ` — ${detail.trancheName}` : ''}
              </DealTitle>
              <SubTitle>
                {[detail?.trancheType, ratings(detail || {} as TrancheDetail), detail?.managerName]
                  .filter(Boolean).join(' · ')}
                {' · '}<span style={{ fontFamily: 'monospace' }}>{position.cusip}</span>
              </SubTitle>
            </PanelHeader>

            <Tabs>
              <TabBtn $active={tab === 'overview'} onClick={() => setTab('overview')}>Overview</TabBtn>
              <TabBtn $active={tab === 'surveillance'} onClick={() => setTab('surveillance')}>Surveillance</TabBtn>
              <TabBtn $active={tab === 'cashflows'} onClick={() => setTab('cashflows')}>Cashflows</TabBtn>
            </Tabs>

            <Body>
              {loading && <Empty>Loading tranche detail…</Empty>}

              {!loading && noData && tab !== 'cashflows' && (
                <Empty>
                  No structured-credit attributes on this position yet.<br />
                  CLO reference data is populated at posting time via the CLO posting SPI.
                </Empty>
              )}

              {/* OVERVIEW */}
              {!loading && !noData && detail && tab === 'overview' && (
                <>
                  <SectionTitle>Capital structure</SectionTitle>
                  {detail.attachmentPoint != null && detail.detachmentPoint != null ? (
                    <>
                      <StackBar>
                        <StackFill
                          $left={pctNum(detail.attachmentPoint)}
                          $width={Math.max(2, pctNum(detail.detachmentPoint) - pctNum(detail.attachmentPoint))}
                        />
                      </StackBar>
                      <StackCaption>
                        Attaches at {pct(detail.attachmentPoint)} · detaches at {pct(detail.detachmentPoint)}
                        {detail.trancheThickness != null ? ` · thickness ${pct(detail.trancheThickness)}` : ''}
                      </StackCaption>
                    </>
                  ) : (
                    <StackCaption>Attachment/detachment not available for this tranche.</StackCaption>
                  )}

                  <Grid style={{ marginTop: '1.1rem' }}>
                    <Field><FLabel>Credit enhancement</FLabel><FValue>{pct(detail.creditEnhancement)}</FValue></Field>
                    <Field><FLabel>Factor</FLabel><FValue>{num(detail.factor, 4)}</FValue></Field>
                    <Field><FLabel>Original face</FLabel><FValue>{money(detail.originalBalance)}</FValue></Field>
                    <Field><FLabel>Current face</FLabel><FValue>{money(detail.currentBalance)}</FValue></Field>
                    <Field><FLabel>Spread</FLabel><FValue>{bps(detail.spreadBps)}</FValue></Field>
                    <Field><FLabel>Coupon type</FLabel><FValue>{detail.couponType || '—'}</FValue></Field>
                    <Field><FLabel>Floor</FLabel><FValue>{detail.floorRate != null ? pct(detail.floorRate) : '—'}</FValue></Field>
                    <Field><FLabel>WAL (deal)</FLabel><FValue>{detail.wal != null ? `${Number(detail.wal).toFixed(1)}y` : '—'}</FValue></Field>
                    <Field><FLabel>Ratings (M/S/F)</FLabel><FValue>{ratings(detail)}</FValue></Field>
                    <Field><FLabel>Payment priority</FLabel><FValue>{detail.paymentPriority ?? '—'}</FValue></Field>
                    <Field><FLabel>Next payment</FLabel><FValue>{detail.nextPaymentDate || '—'}</FValue></Field>
                    <Field><FLabel>CUSIP source</FLabel><FValue>{detail.cusipSource || '—'}</FValue></Field>
                  </Grid>
                </>
              )}

              {/* SURVEILLANCE */}
              {!loading && !noData && detail && tab === 'surveillance' && (
                <>
                  {isEquity && (
                    <StackCaption style={{ marginBottom: '0.85rem' }}>
                      Equity has no binding O/C or I/C test — you're watching the deal's coverage ladder,
                      whose junior-most cushion protects this position.
                    </StackCaption>
                  )}
                  {!isEquity && detail.ocActual != null && (
                    <Grid>
                      <Field>
                        <FLabel>O/C cushion</FLabel>
                        <FValue style={{ color: detail.ocStatus === 'PASS' ? '#34d399' : '#f87171' }}>
                          {detail.ocCushion != null ? Number(detail.ocCushion).toFixed(3) : '—'} {detail.ocStatus}
                        </FValue>
                      </Field>
                      <Field>
                        <FLabel>I/C cushion</FLabel>
                        <FValue style={{ color: detail.icStatus === 'PASS' ? '#34d399' : '#f87171' }}>
                          {detail.icCushion != null ? Number(detail.icCushion).toFixed(3) : '—'} {detail.icStatus}
                        </FValue>
                      </Field>
                    </Grid>
                  )}

                  <SectionTitle>Coverage ladder (deal)</SectionTitle>
                  {ladder.length === 0 && (
                    <Empty>No coverage tests computed yet. Run tests on the deal to populate O/C and I/C.</Empty>
                  )}
                  {ladder.map((t, i) => (
                    <div key={i}>
                      <TestRow>
                        <TestName>{t.trancheName} O/C</TestName>
                        <TestVals $pass={t.ocStatus === 'PASS'}>
                          {Number(t.ocActual).toFixed(3)}x / {Number(t.ocTrigger).toFixed(3)}x · cushion {Number(t.ocCushion).toFixed(3)} · {t.ocStatus}
                        </TestVals>
                      </TestRow>
                      <TestRow>
                        <TestName>{t.trancheName} I/C</TestName>
                        <TestVals $pass={t.icStatus === 'PASS'}>
                          {Number(t.icActual).toFixed(3)}x / {Number(t.icTrigger).toFixed(3)}x · cushion {Number(t.icCushion).toFixed(3)} · {t.icStatus}
                        </TestVals>
                      </TestRow>
                    </div>
                  ))}
                </>
              )}

              {/* CASHFLOWS — honest co-build placeholder */}
              {!loading && tab === 'cashflows' && (
                <CoBuild>
                  <CoBuildTag>Design-partner roadmap</CoBuildTag>
                  <div>
                    Tranche cashflow projection — principal/interest waterfalls under CPR / CDR / loss /
                    prepayment vectors — is the securitized-analytics surface we build <strong>with</strong> a
                    structured-credit design partner, on top of the position-keeping you see in the other tabs.
                  </div>
                  <div style={{ marginTop: '0.7rem', color: '#94a3b8' }}>
                    We integrate or consume existing cashflow libraries (e.g. Intex) until the co-built engine earns the seat.
                  </div>
                </CoBuild>
              )}
            </Body>
          </Panel>
        </>
      )}
    </AnimatePresence>
  );
};

export default CloTrancheDrawer;
